/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/jsx-no-constructed-context-values */
import type { IFinancialsTemplatesQueryVariables } from "components/financials/modern/graphql/__generated__/FinancialsTemplatesQuery.generated";
import { Button } from "components/ui/Button";
import { NativeSelect } from "components/ui/Select";
import classNames from "components/ui/classNames";
import useSelfie from "lib/hooks/useSelfie";
import noop from "lodash/noop";
import { invariant } from "outvariant";
import { createContext, useContext, useId, useState } from "react";
import { FormattedMessage } from "react-intl";
import { IFinancialsTemplateNamespace, IFinancialsTemplatesQueryOrderField, IOrderDirection } from "types/graphqlTypes";
import type { DeepPartial } from "utility-types";
import { Drawer } from "vaul";

export type RefetchFn<Variables> = (variables?: Partial<Variables>) => void;

export const QueryVariablesContext = createContext<
  [
    RefetchFn<IFinancialsTemplatesQueryVariables>,
    Partial<IFinancialsTemplatesQueryVariables | undefined>,
    IFinancialsTemplatesQueryVariables | undefined,
  ]
>([noop, undefined, undefined]);

export default function Filters() {
  const [isOpen, setOpen] = useState(false);

  return (
    <div className="w-full">
      <div className={classNames("flex rounded-xl border border-slate-300 dark:border-transparent md:hidden")}>
        <Drawer.Root open={isOpen} onOpenChange={setOpen}>
          <Drawer.Trigger asChild={true}>
            <Button variant="secondary" size="full" className="rounded-xl bg-slate-100 dark:bg-slate-600">
              <FormattedMessage defaultMessage="Ver filtros" id="CHRwZh" description="Templates gallery filters" />
            </Button>
          </Drawer.Trigger>
          <Drawer.Portal>
            <Drawer.Overlay className="fixed inset-0 z-50 bg-black/50" onClick={() => setOpen(false)} />
            <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 mt-24 flex flex-col rounded-t-xl bg-zinc-100 dark:bg-zinc-800">
              <div className="flex-1 rounded-t-xl p-4">
                <div className="mx-auto mb-6 h-1.5 w-12 flex-shrink-0 rounded-full bg-zinc-300" />

                <div className="mx-auto max-w-lg">
                  <FilterFields onFiltersChange={() => setOpen(false)} />
                </div>
              </div>
            </Drawer.Content>
          </Drawer.Portal>
        </Drawer.Root>
      </div>

      <div
        className={classNames(
          "hidden flex-col gap-4 rounded-xl bg-slate-100 p-4 px-4 dark:bg-slate-900",
          "lg:flex-row lg:gap-6",
          "md:flex",
        )}
      >
        <FilterFields />
      </div>
    </div>
  );
}

interface FilterFieldsProps {
  onFiltersChange?: () => void;
}
function FilterFields({ onFiltersChange }: FilterFieldsProps) {
  const selfie = useSelfie();
  const filterByAuthorId = useId();
  const orderById = useId();
  const [refetch, initialVariables, variables] = useContext(QueryVariablesContext);
  invariant(variables !== undefined, "Variables must be defined");
  invariant(initialVariables !== undefined, "Variables must be defined");

  const setVariables = (nextVariables: DeepPartial<IFinancialsTemplatesQueryVariables>) => {
    invariant(initialVariables, "Variables must be defined");
    refetch({
      ...initialVariables,
      // @ts-ignore
      filters: {
        ...initialVariables.filters,
        ...nextVariables.filters,
      },
      // @ts-ignore
      orderBy: {
        ...initialVariables.orderBy,
        ...nextVariables.orderBy,
      },
      // Reset pagination whenever filters change
      cursor: null,
    });

    if (onFiltersChange) {
      onFiltersChange();
    }
  };

  return (
    <div className="flex flex-col gap-4 lg:flex-row">
      <div className="flex flex-col space-y-2">
        <label className="text-xs font-semibold uppercase text-gray-600 dark:text-slate-400" htmlFor={orderById}>
          <FormattedMessage defaultMessage="Ordenar por" id="aSDX64" description="Templates gallery filters" />
        </label>

        <NativeSelect
          id={orderById}
          value={(() => {
            if (variables.orderBy?.field === IFinancialsTemplatesQueryOrderField.UseCount) {
              return "MOST_USED";
            }
            return variables.orderBy?.direction === IOrderDirection.Descending ? "NEWEST" : "OLDEST";
          })()}
          onChange={(event) => {
            const option = event.currentTarget.value;
            if (option === "MOST_USED") {
              setVariables({
                orderBy: {
                  ...variables.orderBy,
                  field: IFinancialsTemplatesQueryOrderField.UseCount,
                  direction: IOrderDirection.Descending,
                },
              });
              return;
            }

            setVariables({
              ...variables,
              orderBy: {
                ...variables.orderBy,
                field: IFinancialsTemplatesQueryOrderField.CreatedAt,
                direction: option === "NEWEST" ? IOrderDirection.Descending : IOrderDirection.Ascending,
              },
            });
          }}
        >
          <option value="MOST_USED">
            <FormattedMessage defaultMessage="Mais Usados" id="n3qhtU" description="Templates gallery filters" />
          </option>
          <option value="NEWEST">
            <FormattedMessage
              defaultMessage="Criados Recentemente"
              id="+R5xkL"
              description="Templates gallery filters"
            />
          </option>
          <option value="OLDEST">
            <FormattedMessage defaultMessage="Mais Antigos" id="Ovxgsb" description="Templates gallery filters" />
          </option>
        </NativeSelect>
      </div>

      <div className="flex flex-col space-y-2">
        <label className="text-xs font-semibold uppercase text-gray-600 dark:text-slate-400" htmlFor={filterByAuthorId}>
          <FormattedMessage defaultMessage="Filtrar por" id="WaVTkF" description="Templates gallery filters" />
        </label>
        <NativeSelect
          id={filterByAuthorId}
          value={(() => {
            if (variables.filters?.filterByViewerId) {
              return "OWN_TEMPLATES";
            }
            if (
              variables.filters?.filterByNamespace?.length === 1 &&
              variables.filters.filterByNamespace.includes(IFinancialsTemplateNamespace.Fundamentei)
            ) {
              return "FUNDAMENTEI";
            }
            return "ALL";
          })()}
          onChange={(event) => {
            const option = event.currentTarget.value;
            if (option === "ALL") {
              setVariables({
                filters: {
                  ...variables.filters,
                  filterByViewerId: null,
                  filterByNamespace: [IFinancialsTemplateNamespace.Fundamentei, IFinancialsTemplateNamespace.User],
                },
              });
            }
            if (option === "FUNDAMENTEI") {
              setVariables({
                filters: {
                  ...variables.filters,
                  filterByViewerId: null,
                  filterByNamespace: [IFinancialsTemplateNamespace.Fundamentei],
                },
              });
            }
            if (selfie && option === "OWN_TEMPLATES") {
              setVariables({
                filters: {
                  ...variables.filters,
                  filterByViewerId: selfie._id,
                  filterByNamespace: [IFinancialsTemplateNamespace.User],
                },
              });
            }
          }}
        >
          <option value="ALL">
            <FormattedMessage defaultMessage="Todos" id="wk5Rb9" description="Templates gallery filters" />
          </option>
          <option value="FUNDAMENTEI">Fundamentei</option>
        </NativeSelect>
      </div>
    </div>
  );
}
